import React from 'react';
import { formatUsPhone, maskUsPhone } from 'utilities/formatPhone';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import acordLogo from 'images/acordlogo.png';
import CreateNewCarriers from '../CreateNewCarriers';
import AddNewSignature from '../AddNewSignature';
import { useSignatures } from '../../../../hooks/useSignatures';
import { useProducers, fetchAgentContact } from '../ProducerSection';

interface Acord75FormProps { selectedPolicies?: string[]; insuredId?: string; }

export default function Acord75Form({ selectedPolicies = [], insuredId = '' }: Acord75FormProps) {
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);
  const [showAddSignature, setShowAddSignature] = React.useState(false);

  const [agencyName, setAgencyName] = React.useState('');
  const [agencyAddress, setAgencyAddress] = React.useState('');
  
  // Get agencyLocationId from session storage for filtering
  const agencyLocationId = React.useMemo(() => {
    try {
      const stored = sessionStorage.getItem('selectedClient');
      if (stored) {
        const clientData = JSON.parse(stored);
        return clientData?.primaryLocationDatabaseId || undefined;
      }
    } catch {}
    return undefined;
  }, []);
  
  const { producers, producersLoading, producerOptions } = useProducers(agencyLocationId);

  const handleAgencySelect = (id: string) => {
    setAgencyName(id);
    const p = producers.find((x) => x.id === id);
    if (!p) return;
    setAgencyAddress(p.adressLine1 || '');
    setAgencyCity(p.city || '');
    setAgencyState(p.state || '');
    setAgencyZip(p.zip || '');
    if (p.agents && p.agents.length > 0) {
      fetchAgentContact(p.agents[0].databaseId).then(({ phone, fax }) => {
        setAgencyPhone(formatUsPhone(phone));
        setAgencyFax(formatUsPhone(fax));
      });
    }
  };
  const [agencyCity, setAgencyCity] = React.useState('');
  const [agencyState, setAgencyState] = React.useState('');
  const [agencyZip, setAgencyZip] = React.useState('');
  const [agencyPhone, setAgencyPhone] = React.useState('');
  const [agencyFax, setAgencyFax] = React.useState('');
  const [agencyCode, setAgencyCode] = React.useState('');
  const [agencySubCode, setAgencySubCode] = React.useState('');
  const [agencyCustomerId, setAgencyCustomerId] = React.useState('');
  const [insuredName, setInsuredName] = React.useState('');
  const [insuredAddress, setInsuredAddress] = React.useState('');
  const [insuredCity, setInsuredCity] = React.useState('');
  const [insuredState, setInsuredState] = React.useState('');
  const [insuredZip, setInsuredZip] = React.useState('');
  const [companyName, setCompanyName] = React.useState('');
  const [certDate, setCertDate] = React.useState<Date | null>(new Date());
  const [effDate, setEffDate] = React.useState<Date | null>(null);
  const [effTime, setEffTime] = React.useState('1201am');
  const [expDate, setExpDate] = React.useState<Date | null>(null);
  const [extendCoverage, setExtendCoverage] = React.useState(false);
  const [expiringPolicyNo, setExpiringPolicyNo] = React.useState('');
  const [descriptionOfOps, setDescriptionOfOps] = React.useState('');
  const [propBasic, setPropBasic] = React.useState(false);
  const [propBroad, setPropBroad] = React.useState(false);
  const [propSpec, setPropSpec] = React.useState(false);
  const [propCustom1, setPropCustom1] = React.useState('');
  const [propCov1, setPropCov1] = React.useState('');
  const [propDed1, setPropDed1] = React.useState('');
  const [propCoins1, setPropCoins1] = React.useState('');
  const [propAmt1, setPropAmt1] = React.useState('');
  const [propCustom2, setPropCustom2] = React.useState('');
  const [propCov2, setPropCov2] = React.useState('');
  const [propDed2, setPropDed2] = React.useState('');
  const [propCoins2, setPropCoins2] = React.useState('');
  const [propAmt2, setPropAmt2] = React.useState('');
  const [glCGL, setGlCGL] = React.useState(false);
  const [glClaimsMade, setGlClaimsMade] = React.useState(false);
  const [glOccur, setGlOccur] = React.useState(false);
  const [glCustom1Check, setGlCustom1Check] = React.useState(false);
  const [glCustom1Text, setGlCustom1Text] = React.useState('');
  const [glCoverageForms, setGlCoverageForms] = React.useState('');
  const [glRetroDate, setGlRetroDate] = React.useState<Date | null>(null);
  const [glEachOcc, setGlEachOcc] = React.useState('');
  const [glDamageRented, setGlDamageRented] = React.useState('');
  const [glMedExp, setGlMedExp] = React.useState('');
  const [glPersonalAdv, setGlPersonalAdv] = React.useState('');
  const [glGeneralAgg, setGlGeneralAgg] = React.useState('');
  const [glProductsAgg, setGlProductsAgg] = React.useState('');
  const [vlAnyAuto, setVlAnyAuto] = React.useState(false);
  const [vlAllOwned, setVlAllOwned] = React.useState(false);
  const [vlScheduled, setVlScheduled] = React.useState(false);
  const [vlHired, setVlHired] = React.useState(false);
  const [vlNonOwned, setVlNonOwned] = React.useState(false);
  const [vlCustom1Check, setVlCustom1Check] = React.useState(false);
  const [vlCustom1Text, setVlCustom1Text] = React.useState('');
  const [vlCoverageForms, setVlCoverageForms] = React.useState('');
  const [vlCombinedSingle, setVlCombinedSingle] = React.useState('');
  const [vlBiPerson, setVlBiPerson] = React.useState('');
  const [vlBiAccident, setVlBiAccident] = React.useState('');
  const [vlPropertyDamage, setVlPropertyDamage] = React.useState('');
  const [vlMedPayments, setVlMedPayments] = React.useState('');
  const [vlPIP, setVlPIP] = React.useState('');
  const [vlUninsured, setVlUninsured] = React.useState('');
  const [vlCustomLimit, setVlCustomLimit] = React.useState('');
  const [vpdAllVehicles, setVpdAllVehicles] = React.useState(false);
  const [vpdScheduled, setVpdScheduled] = React.useState(false);
  const [vpdACV, setVpdACV] = React.useState(false);
  const [vpdStatedAmt, setVpdStatedAmt] = React.useState(false);
  const [vpdCollisionDed, setVpdCollisionDed] = React.useState('');
  const [vpdOtherDed, setVpdOtherDed] = React.useState('');
  const [vpdCoverageForms, setVpdCoverageForms] = React.useState('');
  const [garAnyAuto, setGarAnyAuto] = React.useState(false);
  const [garCustom1Check, setGarCustom1Check] = React.useState(false);
  const [garCustom1Text, setGarCustom1Text] = React.useState('');
  const [garCoverageForms, setGarCoverageForms] = React.useState('');
  const [garAutoOnly, setGarAutoOnly] = React.useState('');
  const [garOtherThanAuto, setGarOtherThanAuto] = React.useState('');
  const [garEaAccident, setGarEaAccident] = React.useState('');
  const [garAggregate, setGarAggregate] = React.useState('');
  const [exUmbrella, setExUmbrella] = React.useState(false);
  const [exOtherThanUmbrella, setExOtherThanUmbrella] = React.useState(false);
  const [exCoverageForms, setExCoverageForms] = React.useState('');
  const [exRetroDate, setExRetroDate] = React.useState<Date | null>(null);
  const [exEachOcc, setExEachOcc] = React.useState('');
  const [exAggregate, setExAggregate] = React.useState('');
  const [exSelfInsured, setExSelfInsured] = React.useState('');
  const [wcCoverageForms, setWcCoverageForms] = React.useState('');
  const [wcStatutory, setWcStatutory] = React.useState(false);
  const [wcEachAccident, setWcEachAccident] = React.useState('');
  const [wcDiseaseEmployee, setWcDiseaseEmployee] = React.useState('');
  const [wcDiseasePolicyLimit, setWcDiseasePolicyLimit] = React.useState('');
  const [scCoverageForms, setScCoverageForms] = React.useState('');
  const [scFees, setScFees] = React.useState('');
  const [scTaxes, setScTaxes] = React.useState('');
  const [scEstPremium, setScEstPremium] = React.useState('');
  const [holderName, setHolderName] = React.useState('');
  const [holderAddress, setHolderAddress] = React.useState('');
  const [holderCity, setHolderCity] = React.useState('');
  const [holderState, setHolderState] = React.useState('');
  const [holderZip, setHolderZip] = React.useState('');
  const [isMortgagee, setIsMortgagee] = React.useState(false);
  const [isAdditionalInsured, setIsAdditionalInsured] = React.useState(false);
  const [isLossPayee, setIsLossPayee] = React.useState(false);
  const [customInterestCheck, setCustomInterestCheck] = React.useState(false);
  const [customInterestText, setCustomInterestText] = React.useState('');
  const [loanNumber, setLoanNumber] = React.useState('');
  const [authRepSignature, setAuthRepSignature] = React.useState('');
  const { signatureOptions, refetchSignatures } = useSignatures();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = React.useState('');
  React.useEffect(() => {
    if (!authRepSignature) { setSignaturePreviewUrl(''); return; }
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-preview/${authRepSignature}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(url => setSignaturePreviewUrl(url.replace(/^"|"$/g, '')))
      .catch(() => setSignaturePreviewUrl(''));
  }, [authRepSignature]);

  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;
    let cancelled = false;
    const fetchCoverages = async () => {
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';
        const res = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/get-coverages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'NCToken': ncToken,
              Authorization: `Bearer ${igToken}`,
            },
            body: JSON.stringify({
              policiesIds: selectedPolicies,
              truckingCompanyId: insuredId || '',
              fileParentId: '8A083FCC-F0DC-4826-8791-95587FAB20A1',
              naics: [{ Code: '', Name: '' }],
              Page: 'certificateFiller',
            }),
          }
        );
        if (cancelled) return;
        await res.json();
      } catch {
        // silently fail — user can still fill manually
      }
    };
    fetchCoverages();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicies, insuredId]);

  const chk = (id: string, checked: boolean, setter: (v: boolean) => void, label: string) => (
    <div className="masterCert__checkboxItem">
      <Checkbox inputId={id} checked={checked} onChange={(e) => setter(e.checked ?? false)} />
      <label htmlFor={id} className="masterCert__checkboxItemLabel">{label}</label>
    </div>
  );

  const limitRow = (label: string, value: string, setter: (v: string) => void) => (
    <>
      <div className="masterCert__limitLabel">{label}</div>
      <InputText value={value} onChange={(e) => setter(e.target.value)} placeholder="$" className="masterCert__input masterCert__limitInput" />
    </>
  );

  return (
    <>
      <div className="masterCert__section">
        {/* ACORD HEADER */}
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">INSURANCE BINDER</div>
          </div>
          <div className="masterCert__dateWrapper">
            <div className="masterCert__dateLabel">DATE (MM/DD/YYYY)</div>
            <div className="masterCert__dateCell">
              <Calendar value={certDate} onChange={(e) => setCertDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
          </div>
        </div>

        {/* DISCLAIMER */}
        <div className="masterCert__disclaimer">
          This binder is a temporary insurance contract, subject to the conditions shown on the reverse side of this form.
        </div>

        {/* AGENCY + INSURED / COMPANY + DATES */}
        <div className="masterCert__twoColumnLayout">
          {/* LEFT COLUMN */}
          <div>
            {/* AGENCY */}
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__sectionHeader">AGENCY</div>
              <Dropdown value={agencyName} onChange={(e) => handleAgencySelect(e.value)} options={producerOptions} placeholder={producersLoading ? 'Loading...' : 'Select agency'} disabled={producersLoading} filter className="masterCert__input masterCert__inputFull masterCert__inputMbSm" emptyMessage="No available options" emptyFilterMessage="No available options" />
              <InputText maxLength={100} value={agencyAddress} onChange={(e) => setAgencyAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={agencyCity} onChange={(e) => setAgencyCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={50} value={agencyState} onChange={(e) => setAgencyState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={20} value={agencyZip} onChange={(e) => setAgencyZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            {/* PHONE, FAX, CODE, SUBCODE */}
            <div className="masterCert__contactGrid">
              <div>
                <div className="masterCert__contactLabel">PHONE (A/C, No, Ext):</div>
                <InputText maxLength={20} value={agencyPhone} onChange={(e) => setAgencyPhone(maskUsPhone(e.target.value))} className="masterCert__input masterCert__inputFull" />
              </div>
              <div>
                <div className="masterCert__contactLabel">FAX (A/C, No):</div>
                <InputText maxLength={20} value={agencyFax} onChange={(e) => setAgencyFax(maskUsPhone(e.target.value))} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
            <div className="acord75__codeRow">
              <div>
                <div className="masterCert__contactLabel">CODE:</div>
                <InputText maxLength={30} value={agencyCode} onChange={(e) => setAgencyCode(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
              <div>
                <div className="masterCert__contactLabel">SUB CODE:</div>
                <InputText maxLength={30} value={agencySubCode} onChange={(e) => setAgencySubCode(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__contactLabel">AGENCY CUSTOMER ID:</div>
              <InputText maxLength={30} value={agencyCustomerId} onChange={(e) => setAgencyCustomerId(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>

            {/* INSURED */}
            <div>
              <div className="masterCert__sectionHeader">INSURED</div>
              <Dropdown value={insuredName} onChange={(e) => setInsuredName(e.value)} options={[]} placeholder="Type and/or choose location" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" editable disabled emptyMessage="No available options" />
              <InputText maxLength={100} value={insuredAddress} onChange={(e) => setInsuredAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" readOnly />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={insuredCity} onChange={(e) => setInsuredCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" readOnly />
                <InputText maxLength={50} value={insuredState} onChange={(e) => setInsuredState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" readOnly />
                <InputText maxLength={20} value={insuredZip} onChange={(e) => setInsuredZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" readOnly />
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div>
            {/* COMPANY */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>COMPANY</div>
                <Button className="carrierDialog__secondaryBtn" label="Add New" icon="pi pi-plus" outlined size="small" style={{ marginLeft: 8, padding: '2px 10px', fontSize: '0.7rem', height: '28px' }} onClick={() => setShowCreateCarrier(true)} />
              </div>
              <Dropdown value={companyName} onChange={(e) => setCompanyName(e.value)} options={[]} placeholder="Select company" className="masterCert__input masterCert__inputFull" editable emptyMessage="No available options" />
            </div>

            {/* EFFECTIVE & EXPIRATION DATES */}
            <div className="acord75__dateTimeRow">
              <div>
                <div className="masterCert__contactLabel">EFFECTIVE DATE:</div>
                <div className="masterCert__dateCell">
                  <Calendar value={effDate} onChange={(e) => setEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
                </div>
                <div className="acord75__timeRadios">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <RadioButton inputId="a75Time1201" name="effTime" value="1201am" checked={effTime === '1201am'} onChange={(e) => setEffTime(e.value)} />
                    <label htmlFor="a75Time1201" style={{ fontSize: '0.75rem' }}>12:01 AM</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <RadioButton inputId="a75TimeNoon" name="effTime" value="noon" checked={effTime === 'noon'} onChange={(e) => setEffTime(e.value)} />
                    <label htmlFor="a75TimeNoon" style={{ fontSize: '0.75rem' }}>NOON</label>
                  </div>
                </div>
              </div>
              <div>
                <div className="masterCert__contactLabel">EXPIRATION DATE:</div>
                <div className="masterCert__dateCell">
                  <Calendar value={expDate} onChange={(e) => setExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
                </div>
              </div>
            </div>

            {/* EXTEND COVERAGE */}
            <div style={{ marginBottom: 12 }}>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="a75ExtendCov" checked={extendCoverage} onChange={(e) => setExtendCoverage(e.checked ?? false)} />
                <label htmlFor="a75ExtendCov" className="masterCert__checkboxItemLabel">This binder is issued to extend coverage in the above named company</label>
              </div>
            </div>

            {/* PER EXPIRING POLICY # */}
            <div>
              <div className="masterCert__contactLabel">PER EXPIRING POLICY #:</div>
              <InputText maxLength={50} value={expiringPolicyNo} onChange={(e) => setExpiringPolicyNo(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
          </div>
        </div>

        {/* DESCRIPTION OF OPERATIONS */}
        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">DESCRIPTION OF OPERATIONS / VEHICLES / PROPERTY (Including Location)</div>
          <InputTextarea maxLength={2000} value={descriptionOfOps} onChange={(e) => setDescriptionOfOps(e.target.value)} rows={3} className="masterCert__descriptionTextarea" />
        </div>

        {/* ═══ COVERAGES ═══ */}
        <div className="masterCert__coveragesSection">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div className="masterCert__coveragesTitle">COVERAGES</div>
            <div className="masterCert__coveragesTitle">LIMITS</div>
          </div>

          {/* ── PROPERTY ── */}
          <div style={{ borderBottom: '1px solid #e6e7ef', paddingBottom: 12, marginBottom: 12 }}>
            <div className="acord75__covTitle">PROPERTY</div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#333' }}>CAUSES OF LOSS:</span>
              {chk('a75PropBasic', propBasic, setPropBasic, 'BASIC')}
              {chk('a75PropBroad', propBroad, setPropBroad, 'BROAD')}
              {chk('a75PropSpec', propSpec, setPropSpec, 'SPEC')}
            </div>
            <div className="acord75__propHeader">
              <div>TYPE OF INSURANCE</div>
              <div>COVERAGE / FORMS</div>
              <div>DEDUCTIBLE</div>
              <div>COINS %</div>
              <div>AMOUNT</div>
            </div>
            <div className="acord75__propRow">
              <InputText value={propCustom1} onChange={(e) => setPropCustom1(e.target.value)} className="masterCert__input masterCert__inputFull" />
              <InputText value={propCov1} onChange={(e) => setPropCov1(e.target.value)} className="masterCert__input masterCert__inputFull" />
              <InputText value={propDed1} onChange={(e) => setPropDed1(e.target.value)} placeholder="$" className="masterCert__input masterCert__inputFull" />
              <InputText value={propCoins1} onChange={(e) => setPropCoins1(e.target.value)} placeholder="%" className="masterCert__input masterCert__inputFull" />
              <InputText value={propAmt1} onChange={(e) => setPropAmt1(e.target.value)} placeholder="$" className="masterCert__input masterCert__inputFull" />
            </div>
            <div className="acord75__propRow">
              <InputText value={propCustom2} onChange={(e) => setPropCustom2(e.target.value)} className="masterCert__input masterCert__inputFull" />
              <InputText value={propCov2} onChange={(e) => setPropCov2(e.target.value)} className="masterCert__input masterCert__inputFull" />
              <InputText value={propDed2} onChange={(e) => setPropDed2(e.target.value)} placeholder="$" className="masterCert__input masterCert__inputFull" />
              <InputText value={propCoins2} onChange={(e) => setPropCoins2(e.target.value)} placeholder="%" className="masterCert__input masterCert__inputFull" />
              <InputText value={propAmt2} onChange={(e) => setPropAmt2(e.target.value)} placeholder="$" className="masterCert__input masterCert__inputFull" />
            </div>
          </div>

          {/* ── GENERAL LIABILITY ── */}
          <div className="acord75__covSection">
            <div>
              <div className="acord75__covTitle">GENERAL LIABILITY</div>
              {chk('a75GlCGL', glCGL, setGlCGL, 'COMMERCIAL GENERAL LIABILITY')}
              {chk('a75GlClaimsMade', glClaimsMade, setGlClaimsMade, 'CLAIMS MADE')}
              {chk('a75GlOccur', glOccur, setGlOccur, 'OCCUR')}
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="a75GlCustom1" checked={glCustom1Check} onChange={(e) => setGlCustom1Check(e.checked ?? false)} />
                <InputText value={glCustom1Text} onChange={(e) => setGlCustom1Text(e.target.value)} className="masterCert__input masterCert__inputXs" />
              </div>
            </div>
            <div>
              <InputTextarea value={glCoverageForms} onChange={(e) => setGlCoverageForms(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Coverage / Forms" />
              <div style={{ marginTop: 8 }}>
                <div className="masterCert__contactLabel">RETRO DATE FOR CLAIMS MADE:</div>
                <div className="masterCert__dateCell">
                  <Calendar value={glRetroDate} onChange={(e) => setGlRetroDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
                </div>
              </div>
            </div>
            <div className="masterCert__limitsGrid">
              {limitRow('EACH OCCURRENCE', glEachOcc, setGlEachOcc)}
              {limitRow('DAMAGE TO RENTED PREMISES (Ea occurrence)', glDamageRented, setGlDamageRented)}
              {limitRow('MED EXP (Any one person)', glMedExp, setGlMedExp)}
              {limitRow('PERSONAL & ADV INJURY', glPersonalAdv, setGlPersonalAdv)}
              {limitRow('GENERAL AGGREGATE', glGeneralAgg, setGlGeneralAgg)}
              {limitRow('PRODUCTS - COMP/OP AGG', glProductsAgg, setGlProductsAgg)}
            </div>
          </div>

          {/* ── VEHICLE LIABILITY ── */}
          <div className="acord75__covSection">
            <div>
              <div className="acord75__covTitle">VEHICLE LIABILITY</div>
              {chk('a75VlAnyAuto', vlAnyAuto, setVlAnyAuto, 'ANY AUTO')}
              {chk('a75VlAllOwned', vlAllOwned, setVlAllOwned, 'ALL OWNED AUTOS')}
              {chk('a75VlScheduled', vlScheduled, setVlScheduled, 'SCHEDULED AUTOS')}
              {chk('a75VlHired', vlHired, setVlHired, 'HIRED AUTOS')}
              {chk('a75VlNonOwned', vlNonOwned, setVlNonOwned, 'NON OWNED AUTOS')}
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="a75VlCustom1" checked={vlCustom1Check} onChange={(e) => setVlCustom1Check(e.checked ?? false)} />
                <InputText value={vlCustom1Text} onChange={(e) => setVlCustom1Text(e.target.value)} className="masterCert__input masterCert__inputXs" />
              </div>
            </div>
            <div>
              <InputTextarea value={vlCoverageForms} onChange={(e) => setVlCoverageForms(e.target.value)} rows={4} className="masterCert__descriptionTextarea" placeholder="Coverage / Forms" />
            </div>
            <div className="masterCert__limitsGrid">
              {limitRow('COMBINED SINGLE LIMIT (Ea accident)', vlCombinedSingle, setVlCombinedSingle)}
              {limitRow('BODILY INJURY (Per person)', vlBiPerson, setVlBiPerson)}
              {limitRow('BODILY INJURY (Per accident)', vlBiAccident, setVlBiAccident)}
              {limitRow('PROPERTY DAMAGE (Per accident)', vlPropertyDamage, setVlPropertyDamage)}
              {limitRow('MEDICAL PAYMENTS', vlMedPayments, setVlMedPayments)}
              {limitRow('PERSONAL INJURY PROT', vlPIP, setVlPIP)}
              {limitRow('UNINSURED MOTORIST', vlUninsured, setVlUninsured)}
              <InputText value={vlCustomLimit} onChange={(e) => setVlCustomLimit(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <InputText placeholder="$" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* ── VEHICLE PHYSICAL DAMAGE ── */}
          <div className="acord75__covSection">
            <div>
              <div className="acord75__covTitle">VEHICLE PHYSICAL DAMAGE</div>
              {chk('a75VpdAllVeh', vpdAllVehicles, setVpdAllVehicles, 'ALL VEHICLES')}
              {chk('a75VpdScheduled', vpdScheduled, setVpdScheduled, 'SCHEDULED VEHICLES')}
            </div>
            <div>
              <div style={{ marginBottom: 8 }}>
                {chk('a75VpdACV', vpdACV, setVpdACV, 'ACTUAL CASH VALUE')}
                {chk('a75VpdStatedAmt', vpdStatedAmt, setVpdStatedAmt, 'STATED AMOUNT')}
              </div>
              <div className="acord75__dedPair">
                <span>COLLISION</span>
                <span>DED $</span>
                <InputText value={vpdCollisionDed} onChange={(e) => setVpdCollisionDed(e.target.value)} className="masterCert__input" style={{ width: 80 }} />
              </div>
              <div className="acord75__dedPair">
                <span>OTHER THAN COL</span>
                <span>DED $</span>
                <InputText value={vpdOtherDed} onChange={(e) => setVpdOtherDed(e.target.value)} className="masterCert__input" style={{ width: 80 }} />
              </div>
            </div>
            <div>
              <InputTextarea value={vpdCoverageForms} onChange={(e) => setVpdCoverageForms(e.target.value)} rows={4} className="masterCert__descriptionTextarea" placeholder="Coverage / Forms" />
            </div>
          </div>

          {/* ── GARAGE LIABILITY ── */}
          <div className="acord75__covSection">
            <div>
              <div className="acord75__covTitle">GARAGE LIABILITY</div>
              {chk('a75GarAnyAuto', garAnyAuto, setGarAnyAuto, 'ANY AUTO')}
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="a75GarCustom1" checked={garCustom1Check} onChange={(e) => setGarCustom1Check(e.checked ?? false)} />
                <InputText value={garCustom1Text} onChange={(e) => setGarCustom1Text(e.target.value)} className="masterCert__input masterCert__inputXs" />
              </div>
            </div>
            <div>
              <InputTextarea value={garCoverageForms} onChange={(e) => setGarCoverageForms(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Coverage / Forms" />
            </div>
            <div className="masterCert__limitsGrid">
              {limitRow('AUTO ONLY - EA ACCIDENT', garAutoOnly, setGarAutoOnly)}
              {limitRow('OTHER THAN AUTO ONLY', garOtherThanAuto, setGarOtherThanAuto)}
              {limitRow('EACH ACCIDENT', garEaAccident, setGarEaAccident)}
              {limitRow('AGGREGATE', garAggregate, setGarAggregate)}
            </div>
          </div>

          {/* ── EXCESS LIABILITY ── */}
          <div className="acord75__covSection">
            <div>
              <div className="acord75__covTitle">EXCESS LIABILITY</div>
              {chk('a75ExUmbrella', exUmbrella, setExUmbrella, 'UMBRELLA FORM')}
              {chk('a75ExOther', exOtherThanUmbrella, setExOtherThanUmbrella, 'OTHER THAN UMBRELLA FORM')}
            </div>
            <div>
              <InputTextarea value={exCoverageForms} onChange={(e) => setExCoverageForms(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Coverage / Forms" />
              <div style={{ marginTop: 8 }}>
                <div className="masterCert__contactLabel">RETRO DATE FOR CLAIMS MADE:</div>
                <div className="masterCert__dateCell">
                  <Calendar value={exRetroDate} onChange={(e) => setExRetroDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
                </div>
              </div>
            </div>
            <div className="masterCert__limitsGrid">
              {limitRow('EACH OCCURRENCE', exEachOcc, setExEachOcc)}
              {limitRow('AGGREGATE', exAggregate, setExAggregate)}
              {limitRow('SELF-INSURED RETENTION', exSelfInsured, setExSelfInsured)}
            </div>
          </div>

          {/* ── WORKERS COMPENSATION ── */}
          <div className="acord75__covSection">
            <div>
              <div className="acord75__covTitle">WORKER'S COMPENSATION AND EMPLOYER'S LIABILITY</div>
            </div>
            <div>
              <InputTextarea value={wcCoverageForms} onChange={(e) => setWcCoverageForms(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Coverage / Forms" />
              <div style={{ marginTop: 8 }}>
                {chk('a75WcStatutory', wcStatutory, setWcStatutory, 'WC STATUTORY LIMITS')}
              </div>
            </div>
            <div className="masterCert__limitsGrid">
              {limitRow('E.L. EACH ACCIDENT', wcEachAccident, setWcEachAccident)}
              {limitRow('E.L. DISEASE - EA EMPLOYEE', wcDiseaseEmployee, setWcDiseaseEmployee)}
              {limitRow('E.L. DISEASE - POLICY LIMIT', wcDiseasePolicyLimit, setWcDiseasePolicyLimit)}
            </div>
          </div>

          {/* ── SPECIAL CONDITIONS / OTHER COVERAGES ── */}
          <div className="acord75__covSection">
            <div>
              <div className="acord75__covTitle">SPECIAL CONDITIONS / OTHER COVERAGES</div>
            </div>
            <div>
              <InputTextarea value={scCoverageForms} onChange={(e) => setScCoverageForms(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Coverage / Forms" />
            </div>
            <div className="masterCert__limitsGrid">
              {limitRow('FEES', scFees, setScFees)}
              {limitRow('TAXES', scTaxes, setScTaxes)}
              {limitRow('ESTIMATED TOTAL PREMIUM', scEstPremium, setScEstPremium)}
            </div>
          </div>
        </div>

        {/* ═══ NAME AND ADDRESS / AUTH REP ═══ */}
        <div className="acord75__bottomGrid">
          <div>
            <div className="masterCert__sectionHeader">NAME AND ADDRESS</div>
            <InputText maxLength={100} value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
            <InputText maxLength={100} value={holderAddress} onChange={(e) => setHolderAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
            <div className="masterCert__addressGrid">
              <InputText maxLength={100} value={holderCity} onChange={(e) => setHolderCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
              <InputText maxLength={50} value={holderState} onChange={(e) => setHolderState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
              <InputText maxLength={20} value={holderZip} onChange={(e) => setHolderZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
            </div>
            <div className="acord75__checkOptions" style={{ marginTop: 12 }}>
              {chk('a75Mortgagee', isMortgagee, setIsMortgagee, 'MORTGAGEE')}
              {chk('a75AddlInsured', isAdditionalInsured, setIsAdditionalInsured, 'ADDITIONAL INSURED')}
              {chk('a75LossPayee', isLossPayee, setIsLossPayee, 'LOSS PAYEE')}
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="a75CustomInterest" checked={customInterestCheck} onChange={(e) => setCustomInterestCheck(e.checked ?? false)} />
                <InputText value={customInterestText} onChange={(e) => setCustomInterestText(e.target.value)} className="masterCert__input masterCert__inputXs" />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <div className="masterCert__contactLabel">LOAN #:</div>
              <InputText maxLength={30} value={loanNumber} onChange={(e) => setLoanNumber(e.target.value)} className="masterCert__input" style={{ flex: 1 }} />
            </div>
          </div>
          <div>
            <div className="masterCert__authRepLabel">AUTHORIZED REPRESENTATIVE</div>
            <div className="masterCert__authRepRow">
              <Dropdown value={authRepSignature} onChange={(e) => setAuthRepSignature(e.value)} options={signatureOptions} optionLabel="label" optionValue="value" placeholder="Select signature:" className="masterCert__input masterCert__authRepDropdown" emptyMessage="No available options" />
              <Button label="Create New" className="p-button-outlined carrierDialog__secondaryBtn" onClick={() => setShowAddSignature(true)} />
            </div>
              {signaturePreviewUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={signaturePreviewUrl} alt="Signature preview" style={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
                </div>
              )}
          </div>
        </div>

        {/* ACORD Footer */}
        <div className="masterCert__acordFooter">
          <div>ACORD 75 (2010/04)</div>
          <div>&copy; 1988-2014 ACORD CORPORATION. All rights reserved.</div>
        </div>
      </div>

      {/* ═══ PAGE 2: CONDITIONS ═══ */}
      <div className="masterCert__section" style={{ marginTop: 24 }}>
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">INSURANCE BINDER</div>
          </div>
          <div style={{ minWidth: 180 }}>
            <div className="masterCert__contactLabel">AGENCY CUSTOMER ID:</div>
            <InputText maxLength={30} value={agencyCustomerId} readOnly className="masterCert__input masterCert__inputFull" />
          </div>
        </div>

        <div className="acord75__conditionsTitle">CONDITIONS</div>
        <div className="acord75__conditionsText">
          <p>1. This binder is subject to the insuring agreements, exclusions, conditions, and other terms of the policy(ies) currently in use by the company named on the reverse side of this form.</p>
          <p>2. Premium shall be due and payable at the offices of the agency named on the reverse side of this form at the time specified in the policy.</p>
          <p>3. This binder may be cancelled by the insured by surrender of this binder or by written notice to the company stating when the cancellation shall be effective. This binder may be cancelled by the company by notice to the insured in accordance with the policy conditions. It shall be the duty of the company to refund any premium due.</p>
          <p>4. This binder is cancelled when replaced by a policy.</p>
          <p>5. If this binder is not replaced by a policy, the company shall be liable as specified herein for the period the binder was in force.</p>
          <p style={{ marginTop: 12, fontWeight: 600 }}>APPLICABLE IN:</p>
          <p><strong>California:</strong> The obligation imposed by this binder is a premium obligation. Notice of cancellation of the binder by the company must be given to the insured. Such notice of cancellation must comply with all applicable statutes.</p>
          <p><strong>Colorado:</strong> This binder may be cancelled by the company by mailing to the insured named herein, at the address shown, written notice stating when, not less than forty-five (45) days thereafter, such cancellation shall be effective.</p>
          <p><strong>Delaware:</strong> This binder may be cancelled by the company by mailing to the insured named herein, at the address shown, written notice stating when, not less than sixty (60) days thereafter, such cancellation shall be effective, except where there is non-payment of premium, in which case thirty (30) days notice shall be given.</p>
          <p><strong>Florida:</strong> This binder may be cancelled by the company by mailing to the insured named herein, at the address shown, written notice stating when, not less than forty-five (45) days thereafter, such cancellation shall be effective.</p>
          <p><strong>Maryland:</strong> This binder may be cancelled by the company by mailing to the insured named herein, at the address shown, written notice stating when, not less than forty-five (45) days thereafter, or ten (10) days for non-payment of premium, such cancellation shall be effective.</p>
          <p><strong>Michigan:</strong> This binder may be cancelled by the company by mailing to the insured named herein, at the address shown, written notice stating when, not less than ten (10) days thereafter, such cancellation shall be effective.</p>
          <p><strong>Nevada:</strong> This binder may be cancelled by the company by mailing to the insured named herein, at the address shown, written notice stating when, not less than forty-five (45) days thereafter, such cancellation shall be effective, except where there is non-payment of premium, in which case ten (10) days notice shall be given.</p>
          <p><strong>Virgin Islands:</strong> This binder may be cancelled by the company by mailing to the insured named herein, at the address shown, written notice stating when, not less than thirty (30) days thereafter, such cancellation shall be effective.</p>
        </div>

        {/* Page 2 Footer */}
        <div className="masterCert__acordFooter">
          <div>ACORD 75 (2010/04)</div>
          <div>&copy; 1988-2014 ACORD CORPORATION. All rights reserved.</div>
        </div>
      </div>

      <CreateNewCarriers open={showCreateCarrier} onClose={() => setShowCreateCarrier(false)} />
      <AddNewSignature open={showAddSignature} onClose={() => setShowAddSignature(false)} onSaved={refetchSignatures} />
    </>
  );
}
